import { useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import type { CSSObjectWithLabel } from 'react-select';
import Select from 'react-select';
import { Checkbox } from '../presentation/Checkbox';
import { TagStateContext } from '../../contexts/project-tags';
import { ColorSelect } from '../ColorSelect';
import { TagCategory } from '@trinity/chat';
import { TagFormContext } from './TagFormContext';

export type CategoryOption = { value: TagCategory; label: string };

const DefaultCategoryOptions: CategoryOption[] = [
  { value: TagCategory.Transcript, label: 'Transcript Tag' },
  { value: TagCategory.Files, label: 'Files Tag' },
];

type Props = {
  disableParent?: boolean;
  hideParent?: boolean;
  excludeParentIds?: number[];
  /** Override the category options. Defaults to transcript + files. */
  allowedCategories?: CategoryOption[];
  /** Disable color selector (e.g. for Segmentation tags) */
  disableColor?: boolean;
};

export const TagForm = ({
  disableParent = false,
  hideParent = false,
  excludeParentIds = [],
  allowedCategories,
  disableColor = false,
}: Props) => {
  const ctx = useContext(TagFormContext);
  const stateCtx = useContext(TagStateContext);

  if (!ctx) throw new Error('TagForm must be used within a TagFormContainer');
  if (!stateCtx) throw new Error('TagForm must be used within a TagStateContext provider');

  const { data, dispatch } = ctx;
  const { state } = stateCtx;

  const categoryOptions = allowedCategories ?? DefaultCategoryOptions;

  const parentOptions = useMemo(() => {
    return state.tags
      .filter((t) => !excludeParentIds.includes(t.id))
      .map((t) => ({ value: t.id, label: t.base.name }));
  }, [state.tags, excludeParentIds]);

  const parentValue = parentOptions.find((o) => o.value === data.parentId) ?? null;
  const categoryValue = categoryOptions.find((o) => o.value === data.category) ?? null;

  return (
    <>
      <Section>
        <SectionTitle>Tag Title</SectionTitle>
        <StyledInput
          value={data.title}
          onChange={(e) => dispatch({ title: e.target.value })}
          placeholder="Input Title"
        />
      </Section>
      <Section>
        <SectionTitle>Tag Category</SectionTitle>
        <Select
          value={categoryValue ?? { value: null, label: 'None' }}
          options={categoryOptions}
          getOptionLabel={(o) => o.label}
          isClearable
          menuPortalTarget={document.body}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 8 }) as CSSObjectWithLabel }}
          onChange={(v) => dispatch({ category: v?.value ?? null })}
        />
      </Section>
      {!hideParent && (
        <Section>
          <SectionTitle>Parent</SectionTitle>
          <Select
            isDisabled={disableParent}
            value={parentValue ?? { value: null, label: 'None' }}
            options={parentOptions}
            getOptionLabel={(o) => o.label}
            isClearable
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 8 }) as CSSObjectWithLabel }}
            onChange={(v) => dispatch({ parentId: v?.value ?? null })}
          />
        </Section>
      )}
      <Section>
        <SectionTitle>Color</SectionTitle>
        <ColorSelect
          color={data.color}
          onChange={(c) => dispatch({ color: c })}
          id={disableColor ? -1 : undefined}
        />
      </Section>
      <Section>
        <SectionTitle>Include Summary</SectionTitle>
        <Checkbox
          checked={data.includeSummary}
          onChange={(e) => dispatch({ includeSummary: e.target.checked })}
        />
      </Section>
    </>
  );
};

// ─── Styled ─────────────────────────────────────────────────────────────────

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.div`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 10px;
`;

const StyledInput = styled.input`
  height: 38px;
  width: 100%;
  border: 1px solid hsl(0, 0%, 80%);
  border-radius: 4px;
  padding: 0 10px;
  font-size: 16px;
  box-sizing: border-box;
`;
